import { createSlice } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import { IFeeTemplateState } from '../../interface/feeTemplateModel';
import {
  fetchFeeTemplates,
  fetchFeeTemplatesById,
  fetchClassroomAssignedToFeeTemplate,
  updateClassroomAssignedToFeeTemplate,
  fetchFrequency,
  createFeeTemplate,
  updateFeeTemplate,
  deleteFeeTemplate,
  fetchStudentsByFeeTemplate,
} from '../action/feeTemplate.action';

const initialState: IFeeTemplateState = {
  isLoading: false,
  error: null,
  success: false,
  feeTemplates: null,
  feeTemplateDetails: null,
  assignedClassrooms: null,
  frequencies: null,
  students: [],
  message: null,
};

const feeTemplateSlice = createSlice({
  name: 'feeTemplate',
  initialState,
  reducers: {
    resetState: () => initialState,
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.message = null;
    },
    clearStudents: (state) => {
      state.students = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Fee Templates
      .addCase(fetchFeeTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeeTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.feeTemplates = action.payload.data;
        state.error = null;
      })
      .addCase(fetchFeeTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_fee_templates', 'Failed to fetch fee templates');
      })

      // Fetch Fee Template By ID
      .addCase(fetchFeeTemplatesById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeeTemplatesById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.feeTemplateDetails = action.payload;
        state.error = null;
      })
      .addCase(fetchFeeTemplatesById.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_fee_template_details',
            'Failed to fetch fee template details',
          );
      })

      // Fetch Assigned Classrooms
      .addCase(fetchClassroomAssignedToFeeTemplate.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchClassroomAssignedToFeeTemplate.fulfilled, (state, action) => {
        state.assignedClassrooms = action.payload;
        state.error = null;
      })
      .addCase(fetchClassroomAssignedToFeeTemplate.rejected, (state, action) => {
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_fetch_assigned_classrooms',
            'Failed to fetch assigned classrooms',
          );
      })

      // Update Assigned Classrooms
      .addCase(updateClassroomAssignedToFeeTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateClassroomAssignedToFeeTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assignedClassrooms = action.payload;
        state.success = true;
        state.message = i18n.t(
          'toast.classrooms_updated_successfully',
          'Classrooms updated successfully',
        );
        state.error = null;
      })
      .addCase(updateClassroomAssignedToFeeTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error =
          action.payload?.message ||
          i18n.t(
            'toast.failed_to_update_assigned_classrooms',
            'Failed to update assigned classrooms',
          );
      })

      // Fetch Frequencies
      .addCase(fetchFrequency.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchFrequency.fulfilled, (state, action) => {
        state.frequencies = action.payload;
        state.error = null;
      })
      .addCase(fetchFrequency.rejected, (state, action) => {
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_frequencies', 'Failed to fetch frequencies');
      })

      // Create Fee Template
      .addCase(createFeeTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createFeeTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message =
          action.payload.message ||
          i18n.t('toast.fee_template_created_successfully', 'Fee template created successfully');
        state.error = null;
      })
      .addCase(createFeeTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_create_fee_template', 'Failed to create fee template');
      })

      // Update Fee Template
      .addCase(updateFeeTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateFeeTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message =
          action.payload.message ||
          i18n.t('toast.fee_template_updated_successfully', 'Fee template updated successfully');
        state.error = null;
      })
      .addCase(updateFeeTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_update_fee_template', 'Failed to update fee template');
      })

      // Delete Fee Template
      .addCase(deleteFeeTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteFeeTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message =
          action.payload.message ||
          i18n.t('toast.fee_template_deleted_successfully', 'Fee template deleted successfully');
        state.error = null;
      })
      .addCase(deleteFeeTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error =
          action.payload?.message ||
          i18n.t('toast.failed_to_delete_fee_template', 'Failed to delete fee template');
      })

      // Fetch Students by Fee Template
      .addCase(fetchStudentsByFeeTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.students = [];
      })
      .addCase(fetchStudentsByFeeTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.students = action.payload.data.students;
        state.error = null;
      })
      .addCase(fetchStudentsByFeeTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || tr('g.failed_to_fetch_students', 'Failed to fetch students');
        state.students = [];
      });
  },
});

export const { resetState, clearError, clearSuccess, clearStudents } = feeTemplateSlice.actions;

export default feeTemplateSlice.reducer;
